import { ClickAwayListener, List, Paper, Popover, ListItemButton } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import useBoolState from 'app/hooks/useBoolState.ts';
import React from 'react';
import { useSignal } from '@preact/signals-react';
import VariableDialog from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioVariablesSettings/VariableDialog.tsx';
import { useSelector, useDispatch } from 'react-redux';
import * as ScenarioSelectors from 'app/selectors/scenarioSelectors.ts';
import { updateScenarioVariablesPartial } from 'app/actions/scenarios/scenarioAction.ts';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nCommonNs, I18nScenarioEditorNs } from '@/i18n/dictionaries/interfaces.ts';
import { currentUser } from '@/features/user';
import type { VariableSettingsDto } from '@/api-gen/api.schemas';
import { useScenarioVariablesFormContext } from 'app/components/ScenarioEditorPage/ScenarioSettingsDialog/ScenarioVariablesSettings/ScenarioVariablesFormContext.tsx';

type VariableSuggestionsProps = {
  field: React.RefObject<HTMLInputElement | HTMLTextAreaElement>;
  anchorEl: null | HTMLElement;
  setAnchorEl: React.Dispatch<React.SetStateAction<null | HTMLElement>>;
  value: string | undefined;
  setValue: (value: string) => void;
  hasSuggestions: boolean;
  setHasSuggestions: React.Dispatch<React.SetStateAction<boolean>>;
  highlightedIndex: number;
  setHighlightedIndex: React.Dispatch<React.SetStateAction<number>>;
};

const useStyles = makeStyles((theme: Theme) => ({
  suggestions: {
    maxHeight: '300px'
  }
}));

// const regex = /@(\w*)$/;
// const multiRegex = /@(\w*)/g;

// Inspired from https://codesandbox.io/s/magical-browser-dk981?file=/src/App.tsx:557-562

const VariableSuggestions = (props: VariableSuggestionsProps) => {
  const { 
    field, 
    value, 
    setValue, 
    hasSuggestions, 
    setHasSuggestions, 
    anchorEl, 
    setAnchorEl, 
    highlightedIndex,
    setHighlightedIndex
  } = props;
  const classes = useStyles();

  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateCommon] = useTranslation([I18nNamespace.Common]);

  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [editingVariable, setEditingVariable] = React.useState<VariableSettingsDto | null>(null);

  const dispatch = useDispatch();
  const currentScenario = useSelector(ScenarioSelectors.currentChartScenario);
  const variablesContext = useScenarioVariablesFormContext();
  const variables = variablesContext?.variables ?? currentScenario?.variables ?? [];
  const isShared = currentScenario?.companyId === null;

  // Find the position of the @ symbol before the cursor
  const findAtPosition = (text: string, cursorPos: number): number => {
    if (!text) return -1;
    return text.slice(0, cursorPos).lastIndexOf('@');
  };

  // Extract the variable name after the @ symbol
  const extractVariableName = (text: string, atIndex: number): string => {
    if (atIndex === -1 || !text) return '';
    const afterAt = text.slice(atIndex + 1);
    const delimiterMatch = afterAt.match(/[\s.,;:!?()\[\]{}"']/);
    const endIndex = delimiterMatch ? delimiterMatch.index! : afterAt.length;
    return afterAt.slice(0, endIndex);
  };

  // Find the end position of the variable name in the original text
  const findVariableEndPosition = (text: string, atIndex: number): number => {
    if (atIndex === -1 || !text) return atIndex;
    const afterAt = text.slice(atIndex + 1);
    const delimiterMatch = afterAt.match(/[\s.,;:!?()\[\]{}"']/);
    return atIndex + 1 + (delimiterMatch ? delimiterMatch.index! : afterAt.length);
  };

  const valueMatch = React.useMemo(() => {
    if (!value) return '';
    const cursorPos = field.current?.selectionStart ?? value.length;
    const atIndex = findAtPosition(value, cursorPos);
    return extractVariableName(value, atIndex);
  }, [field, value]);

  const suggestions = React.useMemo(() => {
    if (valueMatch) {
      return variables.filter((variable) => variable.name.toLowerCase().includes(valueMatch.toLowerCase()));
    }
    return variables;
  }, [valueMatch, variables]);

  const hasExactSuggestion = React.useMemo(() => {
    return !valueMatch || !!variables.find((variable) => variable.name.toLowerCase() === valueMatch?.toLowerCase());
  }, [valueMatch, variables]);

  const handleClick = (item: string) => {
    if (!value) return;
    const cursorPos = field.current?.selectionStart ?? value.length;
    const atIndex = findAtPosition(value, cursorPos);
    if (atIndex === -1) return;
    
    const endIndex = findVariableEndPosition(value, atIndex);
    const newValue = value.slice(0, atIndex + 1) + item + value.slice(endIndex);
    setValue(newValue);
    setHasSuggestions(false);
    setAnchorEl(null);
    field.current?.focus();
  };

  const onClickAway = () => {
    if (hasSuggestions) {
      setHasSuggestions(false);
      setAnchorEl(null);
      field.current?.focus();
    }
  };

  const addButtonRef = React.useRef<HTMLDivElement>(null);

  const openVariableDialog = () => {
    const canOpenDialog = variablesContext != null || currentScenario?.id !== undefined;
    if (!canOpenDialog) {
      return;
    }

    const fullVariableName = valueMatch;
    const newVariable: VariableSettingsDto = {
      id: null,
      name: fullVariableName,
      values: []
    };

    setEditingVariable(newVariable);
    setDialogOpen(true);
  };

  // Handle Enter key on "Add new variable" button
  React.useEffect(() => {
    if (
      hasSuggestions &&
      highlightedIndex === suggestions.length &&
      (suggestions.length === 0 || !hasExactSuggestion) &&
      addButtonRef.current
    ) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          openVariableDialog();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => {
        window.removeEventListener('keydown', handleKeyDown);
      };
    }
  }, [hasSuggestions, highlightedIndex, suggestions.length, hasExactSuggestion, valueMatch, openVariableDialog]); // Added valueMatch and openVariableDialog

  const selectedCompanyId = useSignal<number | null>(currentUser.value?.companyId ?? null);

  // Handle variable submission to save to the server
  const handleVariableSubmit = (newVar: VariableSettingsDto, editingIndex?: number) => {
    if (variablesContext) {
      if (editingIndex !== undefined) {
        variablesContext.update(editingIndex, newVar);
      } else {
        variablesContext.append(newVar);
      }
      return;
    }

    if (!currentScenario) return;

    const updatedVariables = [...variables];

    if (editingIndex !== undefined) {
      updatedVariables[editingIndex] = newVar;
    } else {
      updatedVariables.push(newVar);
    }

    dispatch(
      updateScenarioVariablesPartial({
        scenarioId: currentScenario.id,
        variables: updatedVariables
      })
    );
  };

  return (
    <>
      <ClickAwayListener onClickAway={onClickAway}>
        <Popover
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          open={hasSuggestions}
          style={{ marginTop: 1 }}
          onClose={onClickAway}
          anchorEl={anchorEl}
          disableAutoFocus
          disableEnforceFocus
          classes={{ paper: classes.suggestions }}
        >
          <List component={Paper}>
            {suggestions.map((variable, index) => (
              <ListItemButton 
                key={variable.name + index} 
                onClick={() => handleClick(variable.name)}
                selected={index === highlightedIndex}
              >
                {variable.name}
              </ListItemButton>
            ))}
            {(suggestions.length === 0 || !hasExactSuggestion) ? (
              <ListItemButton
                key="add"
                onClick={openVariableDialog}
                disabled={variablesContext == null && currentScenario?.id === undefined}
                selected={highlightedIndex === suggestions.length}
                ref={addButtonRef}
              >
                {translate(nameof.full<I18nScenarioEditorNs>((n) => n.variableSuggestions.addNewVariable))} "{valueMatch}"
              </ListItemButton>
            ) : null}
          </List>
        </Popover>
      </ClickAwayListener>
      {(variablesContext != null || currentScenario?.id !== undefined) && (
        <VariableDialog
          open={dialogOpen}
          onClose={() => setDialogOpen(false)}
          onSubmit={(newVar, editingIndex) => {
            // Insert the newly added variable name into the editor text at the @ position
            if (valueMatch && field.current && value) {
              const cursorPos = field.current?.selectionStart ?? value.length;
              const atIndex = findAtPosition(value, cursorPos);
              if (atIndex !== -1) {
                const endIndex = findVariableEndPosition(value, atIndex);
                
                const newValueStr =
                  value.slice(0, atIndex + 1) +
                  newVar.name +
                  value.slice(endIndex);
                setValue(newValueStr);
                setHasSuggestions(false);
                setAnchorEl(null);
              }
            }
            
            // Save the variable to the server
            handleVariableSubmit(newVar, editingIndex);
            
            setDialogOpen(false);
            setEditingVariable(null);
          }}
          isShared={isShared}
          editingIndex={undefined}
          initialVariable={editingVariable ?? undefined}
          selectedCompanyId={selectedCompanyId}
        />
      )}
    </>
  );
};

export default VariableSuggestions;
